import api from './api';
import type {
  LoginCredentials,
  RegisterData,
  User,
  AuthTokens,
  ApiResponse,
  VerifyEmailData,
  ResendVerificationData,
} from '../types';

interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

interface RegisterResponse {
  user: { id: string; email: string; emailVerified: boolean };
  message: string;
}

interface VerifyEmailResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export async function login(
  credentials: LoginCredentials
): Promise<ApiResponse<AuthResponse>> {
  const response = await api.post<ApiResponse<AuthResponse>>(
    '/auth/login',
    credentials
  );
  return response.data;
}

export async function register(
  data: RegisterData
): Promise<ApiResponse<RegisterResponse>> {
  const response = await api.post<ApiResponse<RegisterResponse>>(
    '/auth/register',
    data
  );
  return response.data;
}

export async function verifyEmail(
  data: VerifyEmailData
): Promise<ApiResponse<VerifyEmailResponse>> {
  const response = await api.post<ApiResponse<VerifyEmailResponse>>(
    '/auth/verify-email',
    data
  );
  return response.data;
}

export async function resendVerification(
  data: ResendVerificationData
): Promise<ApiResponse<{ message: string }>> {
  const response = await api.post<ApiResponse<{ message: string }>>(
    '/auth/resend-verification',
    data
  );
  return response.data;
}

export async function refreshTokens(
  refreshToken: string
): Promise<ApiResponse<AuthTokens>> {
  const response = await api.post<ApiResponse<AuthTokens>>('/auth/refresh', {
    refreshToken,
  });
  return response.data;
}

export async function logout(
  userId: string,
  refreshToken?: string
): Promise<void> {
  await api.post('/auth/logout', { userId, refreshToken });
}

export async function subscribePrelaunch(
  email: string
): Promise<ApiResponse<void>> {
  const response = await api.post<ApiResponse<void>>('/prelaunch/subscribe', {
    email,
  });
  return response.data;
}

export async function forgotPassword(
  email: string
): Promise<ApiResponse<{ message: string }>> {
  const response = await api.post<ApiResponse<{ message: string }>>(
    '/auth/forgot-password',
    { email }
  );
  return response.data;
}

export async function resetPassword(
  token: string,
  password: string
): Promise<ApiResponse<{ email: string }>> {
  const response = await api.post<ApiResponse<{ email: string }>>(
    '/auth/reset-password',
    { token, password }
  );
  return response.data;
}

// Types pour la restauration de compte
export interface RestoreAccountCheckResponse {
  email: string;
  firstName: string | null;
  lastName: string | null;
  daysRemaining: number;
  expiresAt: string;
  canRestore: boolean;
}

export interface RestoreAccountResponse {
  restoredId: string;
  warnings?: string[];
}

/**
 * Verifie si un token de restauration est valide
 */
export async function checkRestoreToken(
  token: string
): Promise<ApiResponse<RestoreAccountCheckResponse>> {
  const response = await api.get<ApiResponse<RestoreAccountCheckResponse>>(
    `/auth/restore-account/check?token=${encodeURIComponent(token)}`
  );
  return response.data;
}

/**
 * Restaure un compte via le token (lien email)
 */
export async function restoreAccountByToken(
  token: string
): Promise<ApiResponse<RestoreAccountResponse>> {
  const response = await api.post<ApiResponse<RestoreAccountResponse>>(
    '/auth/restore-account/token',
    { token }
  );
  return response.data;
}

/**
 * Restaure un compte via les identifiants
 */
export async function restoreAccountByCredentials(
  email: string,
  password: string
): Promise<ApiResponse<RestoreAccountResponse>> {
  const response = await api.post<ApiResponse<RestoreAccountResponse>>(
    '/auth/restore-account/credentials',
    { email, password }
  );
  return response.data;
}

/**
 * Renvoie par email le lien de récupération d'un compte supprimé.
 * La réponse est identique qu'un tel compte existe ou non.
 */
export async function resendRestoreLink(
  email: string
): Promise<ApiResponse<void>> {
  const response = await api.post<ApiResponse<void>>(
    '/auth/restore-account/resend',
    { email }
  );
  return response.data;
}

/** Supprime une demande de pré-inscription depuis le lien des emails */
export async function unsubscribePrelaunch(
  token: string
): Promise<ApiResponse<void>> {
  const response = await api.get<ApiResponse<void>>(
    `/prelaunch/unsubscribe/${token}`
  );
  return response.data;
}
